import React from "react";
import { useTranslation } from "react-i18next";
import "./AboutMethod.css";

/**
 * UniTech Engineering — Page À propos / 05 Notre méthode.
 * Composant autonome, styles isolés et textes FR/EN intégrés.
 * Ne modifie aucune section existante ni les traductions globales.
 */
const CONTENT = {
  fr: {
    eyebrow: "NOTRE MANIÈRE DE TRAVAILLER",
    titleFirst: "De l’idée au terrain,",
    titleAccent: "avec méthode.",
    stepsLabel: "Les quatre étapes de notre méthode",
    steps: [
      {
        number: "01",
        category: "ÉCOUTE",
        title: "Comprendre",
        description: "Vos équipements, contraintes et objectifs."
      },
      {
        number: "02",
        category: "INGÉNIERIE",
        title: "Concevoir",
        description: "Développer une réponse pensée pour son usage réel."
      },
      {
        number: "03",
        category: "VÉRIFICATION",
        title: "Tester",
        description: "Contrôler les fonctions et documenter les limites."
      },
      {
        number: "04",
        category: "MISE EN ŒUVRE",
        title: "Accompagner",
        description: "Faciliter le déploiement et faire progresser les solutions."
      }
    ],
    panelTitle: "Un partenaire technique, de la conception à l’intégration.",
    panelDescription:
      "Des échanges structurés et des validations adaptées à la réalité de votre projet.",
    cta: "Présenter votre besoin"
  },
  en: {
    eyebrow: "HOW WE WORK",
    titleFirst: "From concept to the field,",
    titleAccent: "with a clear method.",
    stepsLabel: "The four stages of our approach",
    steps: [
      {
        number: "01",
        category: "LISTENING",
        title: "Understand",
        description: "Your equipment, constraints and objectives."
      },
      {
        number: "02",
        category: "ENGINEERING",
        title: "Design",
        description: "Develop an approach suited to real-world use."
      },
      {
        number: "03",
        category: "VERIFICATION",
        title: "Test",
        description: "Check functions and document limitations."
      },
      {
        number: "04",
        category: "IMPLEMENTATION",
        title: "Support",
        description: "Help with deployment and improve solutions over time."
      }
    ],
    panelTitle: "A technical partner, from design to integration.",
    panelDescription:
      "Structured discussions and validation steps tailored to the realities of your project.",
    cta: "Tell us about your project"
  }
};

export default function AboutMethod() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = CONTENT[language] || CONTENT.fr;

  return (
    <section
      id="about-method"
      className="about-method"
      aria-labelledby="about-method-title"
    >
      <div className="about-method__container">
        <div className="about-method__grid">
          <div className="about-method__main">
            <header className="about-method__header">
              <p className="about-method__eyebrow">{copy.eyebrow}</p>
              <h2 className="about-method__title" id="about-method-title">
                <span>{copy.titleFirst}</span>
                <span className="about-method__titleAccent">{copy.titleAccent}</span>
              </h2>
            </header>

            <ol className="about-method__steps" aria-label={copy.stepsLabel}>
              {copy.steps.map((step) => (
                <li className="about-method__step" key={step.number}>
                  <p className="about-method__stepEyebrow">
                    {step.number} / {step.category}
                  </p>
                  <h3 className="about-method__stepTitle">{step.title}</h3>
                  <p className="about-method__stepDescription">
                    {step.description}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <aside className="about-method__panel" aria-label={copy.panelTitle}>
            <div className="about-method__panelDecor" aria-hidden="true">
              <svg viewBox="0 0 90 90" fill="none" focusable="false">
                <circle cx="45" cy="45" r="31" stroke="currentColor" strokeWidth="6" />
                <circle cx="45" cy="45" r="13" stroke="currentColor" strokeWidth="6" />
              </svg>
            </div>

            <div className="about-method__panelContent">
              <h3 className="about-method__panelTitle">{copy.panelTitle}</h3>
              <p className="about-method__panelDescription">
                {copy.panelDescription}
              </p>
              <a href="/contact" className="about-method__button">
                <span>{copy.cta}</span>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M6 18 18 6M9 6h9v9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
